// Axios 客户端 + JWT 拦截器
import axios, { AxiosError, type AxiosInstance } from 'axios';
import { useAuthStore } from '@/store/auth';
import type { ApiErrorBody, ApiErrorCode } from './types';

export const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 15_000,
  headers: { 'content-type': 'application/json' },
});

// 请求拦截:加 Bearer token
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截:把后端错误统一成 ApiError
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: ApiErrorCode,
    message: string,
    public field?: string,
    public retry_after_secs?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

apiClient.interceptors.response.use(
  (resp) => resp,
  (err: AxiosError<ApiErrorBody>) => {
    if (err.response) {
      const body = err.response.data;
      const code = (body?.error?.code ?? 'internal') as ApiErrorCode;
      const message = body?.error?.message ?? err.message;
      const field = body?.error?.field;
      const retryAfterSecs = body?.error?.retry_after_secs;
      return Promise.reject(
        new ApiError(err.response.status, code, message, field, retryAfterSecs),
      );
    }
    // 网络错误
    return Promise.reject(new ApiError(0, 'internal', err.message));
  },
);

/** 把后端错误码映射成 i18n 友好的中文消息(简单版,Phase 4 不做 i18n) */
export function explainError(err: unknown): string {
  if (err instanceof ApiError) {
    // 一些常见码做特别提示
    switch (err.code) {
      case 'user_already_exists':
        return '用户名或邮箱已被占用';
      case 'invalid_credentials':
        return '用户名/邮箱或密码错误';
      case 'unauthenticated':
        return '请先登录';
      case 'permission_denied':
        return '权限不足';
      case 'validation_failed':
        return err.field ? `${err.field}: ${err.message}` : `参数错误: ${err.message}`;
      case 'rate_limited': {
        const secs = err.retry_after_secs ?? 0;
        if (secs > 0) {
          return `操作太频繁,请 ${secs} 秒后再试`;
        }
        return '操作太频繁,请稍后再试';
      }
      default:
        return err.message;
    }
  }
  if (err instanceof Error) return err.message;
  return '未知错误';
}
